<template>
  <div class="hotel">
    <div class="hotel__thumbnail bg-white mb-4 round">
      <Thumbnail
        v-if="hotel.jsonForIntegrateWithGallery.coBrand"
        :id="hotel.code"
        :first-sub-gallery-id="hotel.firstSubGalleryId()"
        :thumbnail="hotel.defaultImage"
        :has-icon="hotel.hasGallery()"
        shadow
        gradient
        :logo="hotel.jsonForIntegrateWithGallery.coBrand"
        :imagePosition="imagePosition"
        :triggerAttributes="triggerAttributes"
      />

      <Thumbnail
        v-else
        :id="hotel.code"
        :first-sub-gallery-id="hotel.firstSubGalleryId()"
        :thumbnail="hotel.defaultImage"
        :has-icon="hotel.hasGallery()"
        shadow
        gradient
        :logo="hotel.brandCode"
        :imagePosition="imagePosition"
        :triggerAttributes="triggerAttributes"
      />
      <HotelGalleryModal v-if="hotel.hasGallery()" :hotel="hotel.jsonHotel()" />
      <div class="hotel__info">
        <div class="hotel__title-box">
          <LargeText class="hotel__title">{{ hotel.name }}</LargeText>
        </div>
        <div class="hotel__tags">
          <span class="hotel__tag hotel__tag--discount" v-if="hotel.maxDiscount !== 0">
            {{ tagDiscount }}
          </span>
          <span class="hotel__tag hotel__tag--pay-at-property" v-if="hotel.isPayUponArrival">
            {{ translate('rdIsPayUponArrival') }}
          </span>
          <span
            class="hotel__tag hotel__tag--crowd"
            v-if="customerCrowd === crowdTypes.TRIPLE_POINTS && !isNewCorporateProgramActive"
          >
            {{ translate('rdYouCanEarnTriplePoints') }}
          </span>
          <span
            class="hotel__tag hotel__tag--crowd"
            v-if="isSomeStayWithCrowd() && !isNewCorporateProgramActive"
          >
            {{ translate('rd20DiscountCrowd') }}
          </span>
          <span class="hotel__tag hotel__tag--alert-cyan" v-if="hotel && hotel.newlyRenovated">
            <i class="color-white icon-star xs mr-1" />
            {{ translate('rdNewlyRenovated') }}
          </span>
        </div>
        <div class="hotel__details">
          <div class="hotel__website--mobile">
            <a class="hotel__link" :href="hotel.link" target="_blank" rel="noopener noreferrer">
              {{ translate('rdVisitHotel')
              }}<i class="icon icon-open-url-icon xs align-baseline"></i>
            </a>
          </div>
          <div class="hotel__address">
            <a
              class="hotel__address-link"
              :href="`${hotel.contactURL}#entity-map`"
              target="_blank"
              rel="noopener noreferrer"
              >{{ hotel.address.format() }}</a
            >
          </div>
          <div class="hotel__hotel-phone">
            <i class="icon icon-phone xs align-baseline"></i>
            <a :href="'tel:' + hotel.phone">
              <MediumText class="hotel__phone">{{ hotel.phone }}</MediumText>
            </a>
          </div>
          <div class="hotel__website--desktop">
            <a class="hotel__link" :href="hotel.link" target="_blank" rel="noopener noreferrer">
              {{ translate('rdVisitHotel')
              }}<i class="icon icon-open-url-icon xs align-baseline"></i>
            </a>
          </div>
          <div v-if="tripAdvisorInfo" class="hotel__tripAdvisor">
            <TripAdvisorPreview
              :rating="tripAdvisorInfo.rating.bubbleRating"
              :review-count="tripAdvisorInfo.rating.reviewCount"
            />
          </div>
          <div
            v-if="hotel && hasNotificationMessages(hotel.notificationMessages)"
            class="hotel__info-hotel"
          >
            <InfoHotelComponent :messages="hotel.notificationMessages" />
          </div>
        </div>
      </div>
    </div>
    <div class="hotel__map bg-white">
      <AzurePreviewMap
        v-if="isChina"
        :title="hotel.name"
        :marker="hotel.brandCode"
        :coordinates="hotel.coordinates"
      />
      <PreviewMap
        v-else
        :title="hotel.name"
        :marker="hotel.brandCode"
        :coordinates="hotel.coordinates"
      />
    </div>
  </div>
</template>

<script>
import Hotel from '../../domain/Hotel'
import InfoHotelComponent from './InfoHotelComponent'
import TripAdvisor from '../../domain/TripAdvisor'
import Thumbnail from '../shared/Thumbnail'
import HotelGalleryModal from '../modal/HotelGalleryModal'
import LargeText from '../shared/LargeText'
import MediumText from '../shared/MediumText'
import PreviewMap from '../PreviewMap'
import AzurePreviewMap from '../AzurePreviewMap'
import {
  CrowdTypes,
  isChina,
  localePathException,
  isTurkish,
  isHebrew,
  isRtl
} from '@/infrastructure/Configuration'
import TripAdvisorPreview from '../tripadvisor/TripAdvisorPreview'
import { createNamespacedHelpers, mapGetters as mapGettersGeneral } from 'vuex'
import { getHotelGalleryTriggers, getAllGalleryImagesCount } from '@/infrastructure/Utils'

const { mapGetters } = createNamespacedHelpers('multiRoomDisplay')

export default {
  name: 'HotelCardComponent',
  components: {
    LargeText,
    TripAdvisorPreview,
    PreviewMap,
    AzurePreviewMap,
    Thumbnail,
    InfoHotelComponent,
    HotelGalleryModal,
    MediumText
  },
  props: {
    hotel: {
      type: Hotel,
      default: null
    },
    tripAdvisorInfo: {
      type: TripAdvisor
    },
    customerCrowd: {
      type: String
    }
  },
  data: () => ({
    crowdTypes: CrowdTypes,
    isChina: isChina
  }),
  computed: {
    ...mapGetters(['filteredHotel']),
    ...mapGettersGeneral(['isNewCorporateProgramActive']),
    triggerAttributes() {
      return getHotelGalleryTriggers(this.hotel)
    },
    imagePosition() {
      if (
        this.hotel.jsonForIntegrateWithGallery &&
        this.hotel.jsonForIntegrateWithGallery.galleries &&
        this.hotel.jsonForIntegrateWithGallery.galleries.length >= 1
      ) {
        return getAllGalleryImagesCount(this.hotel.jsonForIntegrateWithGallery.galleries)
      }
      return ''
    },
    tagDiscount() {
      const discount = this.hotel.maxDiscount.toString()
      const savingText = this.translate('rdSaving')

      const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1)
      const capitalizeText = capitalize(savingText)

      if (localePathException()) {
        return `${capitalizeText} ${discount}%`
      }

      if (isTurkish() || isHebrew()) {
        return `${capitalizeText} %${discount}`
      }

      if (isRtl()) {
        return `%${discount} ${capitalizeText}`
      }

      return `${discount}% ${capitalizeText}`
    }
  },
  methods: {
    isSomeStayWithCrowd() {
      const isAnyCrowdDiscount = Boolean(
        this.filteredHotel.rooms.filter(room =>
          room.groups.find(group => group.selectedStay?.crowdDiscount)
        ).length
      )
      const hasCashOrPointsDiscount =
        Boolean(
          this.filteredHotel.rooms.filter(room =>
            room.groups.find(group => group.selectedStay?.cash?.undiscountedAmountPerNight > 0)
          ).length
        ) ||
        Boolean(
          this.filteredHotel.rooms.filter(room =>
            room.groups.find(group => group.selectedStay?.points?.undiscountedAmountPerNight > 0)
          ).length
        )
      return (
        this.customerCrowd === this.crowdTypes.DISCOUNT &&
        isAnyCrowdDiscount &&
        hasCashOrPointsDiscount
      )
    },
    hasNotificationMessages(messages) {
      return messages.filter(message => message?.text?.trim() !== '')?.length > 0
    }
  }
}
</script>

<style lang="scss" scoped>
.hotel {
  &__thumbnail {
    border-radius: 8px;
    @media (min-width: 992px) {
      overflow: hidden;
    }
  }

  &__info {
    padding: 1rem 2.5rem 1rem 2.5rem;
    position: relative;
    @media (min-width: 992px) {
      padding: 1.5rem 1rem;
    }
  }

  &__title-box {
    min-height: 35px;
    display: flex;
    position: absolute;
    top: -10rem;
    @media (min-width: 992px) {
      position: initial;
    }
  }

  &__title {
    align-self: flex-end;
    color: white;
    @media (min-width: 992px) {
      color: black;
    }
  }

  &__tags {
    display: flex;
    align-items: flex-start;
    margin-top: -2.2rem;
    margin-bottom: 1rem;
    margin-right: 1.5rem;
    flex-wrap: wrap;
    @media (min-width: 992px) {
      position: initial;
      margin-top: 1rem;
      flex-direction: column;
    }
  }

  &__tag {
    padding: 0.1rem 1rem;
    border-radius: 0.25rem;
    font-size: 1.12rem;
    font-weight: bold;
    margin-top: 0.25rem;
    margin-right: 1rem;
    color: white;
    @media (min-width: 992px) {
      margin-bottom: 0.25rem;
    }
    &--discount {
      background-color: #f5a623;
    }
    &--pay-at-property {
      background-color: #126409;
    }
    &--alert-cyan {
      background-color: #60b7b7;
    }
    &--crowd {
      background-color: #382246;
    }
  }

  &__address {
    position: absolute;
    top: -6rem;
    width: 80%;
    color: white;
    @media (min-width: 992px) {
      position: initial;
      color: dimgrey;
    }
  }

  &__address-link {
    font-size: 12px;
    color: white;
    @media (min-width: 992px) {
      color: black;
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
  }

  &__phone {
    color: dimgrey;
    margin-left: 1rem;
  }

  &__link {
    color: black;
    text-decoration: underline;
    right: 1rem;
    margin-top: 1rem;

    i {
      margin-left: 1rem;
    }

    @media (min-width: 992px) {
      color: black;
      text-decoration: underline;
      /*position: initial;*/

      i {
        margin-left: 1rem;
      }
    }
  }

  &__website {
    margin: 0.5rem 0 0 0;
    &--desktop {
      display: none;
      @media (min-width: 992px) {
        display: block;
      }
    }
    &--mobile {
      display: flex;
      justify-content: flex-end;
      @media (min-width: 992px) {
        display: none;
      }
    }
  }

  &__tripAdvisor {
    display: block;
    margin-left: -1rem;
    margin-bottom: 0.5rem;
    @media (min-width: 992px) {
      display: none;
    }
  }

  &__info-hotel {
    display: block;
    padding-bottom: 1rem;
    @media (min-width: 992px) {
      display: none;
    }
  }

  &__map {
    display: none;
    overflow: hidden;
    @media (min-width: 992px) {
      display: block;
      border-radius: 8px;
    }
  }
}
html[dir='rtl'] {
  .hotel {
    &__info {
      text-align: right;
    }
    &__tags {
      margin-right: -1rem;
    }
    &__link {
      i {
        margin-right: 0.5rem;
        transform: scaleX(-1);
      }
    }
  }
}
</style>
